import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Zap,
  Pause,
  Play,
  SkipForward,
  Square,
  Loader2,
  PhoneCall,
  Bot,
  PhoneOff,
} from 'lucide-react';
import { usePowerDialer } from '@/hooks/usePowerDialer';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import type { OutboundContact } from '@/hooks/useOutboundQueue';

interface PowerDialerBarProps {
  queue: OutboundContact[];
  batchLabel?: string | null;
}

const attemptTone: Record<string, string> = {
  dialing: 'bg-blue-50 text-blue-700 border-blue-200',
  ringing: 'bg-amber-50 text-amber-700 border-amber-200',
  connected: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const outcomeLabel: Record<string, string> = {
  human: 'Humano',
  machine: 'Caixa postal',
  no_answer: 'Não atendeu',
  busy: 'Ocupado',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

export function PowerDialerBar({ queue, batchLabel }: PowerDialerBarProps) {
  const [concurrency, setConcurrency] = useState('3');
  const { data: currentUser } = useCurrentAppUser();
  const hasDedicatedNumber = !!currentUser?.twilio_phone_number;
  const { session, attempts, liveAttempts, connectedAttempt, isBusy, start, pause, resume, skip, end } =
    usePowerDialer({ autoManage: false });

  const handleEnd = async () => {
    await end();
  };

  const eligibleIds = useMemo(
    () =>
      queue
        .filter(
          (item) =>
            item.status === 'pending' &&
            item.contact?.primary_phone &&
            !item.contact?.do_not_call,
        )
        .map((item) => item.contact_id),
    [queue]
  );

  const isActive = session?.status === 'active';
  const isPaused = session?.status === 'paused';

  const recent = useMemo(() => attempts.filter((a) => a.status === 'completed').slice(0, 5), [attempts]);

  const nameOf = (a: (typeof attempts)[number]) =>
    [a.contact?.first_name, a.contact?.last_name].filter(Boolean).join(' ') || a.to_e164;

  return (
    <div className="rounded-xl border bg-muted/30 px-4 py-3 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-orange-500" />
          <span className="text-sm font-semibold">Power Dialer</span>
          {session && (
            <Badge variant="outline" className="rounded-full text-xs">
              {isActive ? 'Discando' : isPaused ? 'Pausado' : session.status}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!session && (
            <>
              <Select value={concurrency} onValueChange={setConcurrency}>
                <SelectTrigger className="h-8 w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 linha por vez</SelectItem>
                  <SelectItem value="2">2 simultâneas</SelectItem>
                  <SelectItem value="3">3 simultâneas</SelectItem>
                  <SelectItem value="4">4 simultâneas</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="h-8 gap-1.5"
                disabled={isBusy || eligibleIds.length === 0 || !hasDedicatedNumber}
                title={
                  hasDedicatedNumber
                    ? undefined
                    : 'Seu usuário não tem número dedicado configurado — fale com o administrador'
                }
                onClick={() => start(eligibleIds, { concurrency: Number(concurrency), batchLabel })}
              >
                {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Iniciar ({eligibleIds.length})
              </Button>

            </>
          )}

          {isActive && (
            <>
              <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={isBusy} onClick={skip}>
                <SkipForward className="h-3.5 w-3.5" /> Pular
              </Button>
              <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={isBusy} onClick={pause}>
                <Pause className="h-3.5 w-3.5" /> Pausar
              </Button>
            </>
          )}

          {isPaused && (
            <Button size="sm" className="h-8 gap-1.5" disabled={isBusy} onClick={resume}>
              <Play className="h-3.5 w-3.5" /> Retomar
            </Button>
          )}

          <Button size="sm" variant="ghost" className="h-8" asChild>
            <Link to="/outbound/discador/sessoes">Histórico</Link>
          </Button>

          {session && (
            <Button size="sm" variant="destructive" className="h-8 gap-1.5" disabled={isBusy} onClick={handleEnd}>
              <Square className="h-3.5 w-3.5" /> Encerrar
            </Button>
          )}
        </div>
      </div>

      {!session && !hasDedicatedNumber && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Seu usuário não tem número dedicado configurado. O Power Dialer só disca com o número do próprio SDR — fale
          com o administrador para atribuir um número.
        </p>
      )}


      {session && (
        <>
          <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1">
              <PhoneCall className="h-3 w-3" /> Discadas: <strong>{session.total_dialed}</strong>
            </span>
            <span className="flex items-center gap-1 text-emerald-600">
              Humanos: <strong>{session.total_human}</strong>
            </span>
            <span className="flex items-center gap-1">
              <Bot className="h-3 w-3" /> Caixa postal: <strong>{session.total_machine}</strong>
            </span>
            <span>Fila restante: <strong>{Math.max(0, (session.queue_contact_ids?.length ?? 0) - session.total_dialed)}</strong></span>
          </div>

          {connectedAttempt && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2">
              <PhoneCall className="h-4 w-4" />
              Em conversa com <strong>{nameOf(connectedAttempt)}</strong> — {connectedAttempt.to_e164}
            </div>
          )}

          {liveAttempts.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {liveAttempts.map((a) => (
                <span
                  key={a.id}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                    attemptTone[a.status] ?? 'bg-muted text-muted-foreground'
                  }`}
                >
                  <PhoneCall className="h-3 w-3" />
                  {nameOf(a)}
                  <span className="opacity-70">
                    {a.status === 'dialing' ? 'discando' : a.status === 'ringing' ? 'tocando' : 'conectado'}
                  </span>
                </span>
              ))}
            </div>
          )}

          {recent.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {recent.map((a) => (
                <span
                  key={a.id}
                  className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs text-muted-foreground"
                >
                  <PhoneOff className="h-3 w-3" />
                  {nameOf(a)}
                  <span className="opacity-70">{outcomeLabel[a.outcome ?? ''] ?? a.outcome ?? '—'}</span>
                </span>
              ))}
            </div>
          )}
        </>
      )}

    </div>
  );
}
